import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";
import { featureSpecs, type FeatureSpec } from "./feature-templates";
import {
  addFieldsToEntity,
  addRelationsToEntity,
  applyFeature,
  isPlanEmpty,
  planFeature,
  resolveRole,
  type ActualEntity,
  type RoleBindings,
} from "./feature-reconciler";

export type { FeatureName } from "./feature-templates";

/**
 * Flattened view of the feature declarations for the Features UI. The UI only
 * needs identity and copy; everything about *shape* lives in `feature-templates`
 * and is interpreted by the reconciler.
 */
export const templatesForAdminDashboard = featureSpecs.map((feature) => ({
  entityName: feature.key,
  name: feature.name,
  description: feature.description,
  optional: feature.optional,
}));

/**
 * The entity currently filling a feature role, or undefined when the tenant has
 * no such entity. Consumers outside the reconciler resolve through this instead
 * of matching a name, so a rebound or renamed entity stays reachable.
 */
export const resolveFeatureEntity = <T extends { id: string; name: string }>(
  role: string,
  entities: Array<T> | undefined,
  bindings?: RoleBindings,
): T | undefined => {
  if (!entities) return undefined;
  const spec = featureSpecs.flatMap((feature) => feature.entities).find((e) => e.role === role);
  return spec ? resolveRole(spec, entities, bindings) : undefined;
};

export interface TemplateCheckResult {
  entityExists: boolean;
  entityId?: string;
  /** True when the tenant already matches everything the feature declares. */
  satisfied: boolean;
  missingFields: Array<{
    name: string;
    data_type: string;
  }>;
  missingRelations: Array<{
    field_name: string;
    type: "OneToOne" | "OneToMany" | "ManyToOne" | "ManyToMany";
    target_entity_id: string;
    target_field_name?: string;
  }>;
}

/**
 * A feature counts as activated once the tenant matches its declaration with
 * nothing left to reconcile. Shared by the Features UI and `useIsFeatureActive`.
 */
export const isTemplateActive = (result: TemplateCheckResult | undefined): boolean =>
  Boolean(result?.satisfied);

export interface AdminDashboardCheckResult {
  folderStructureExists: boolean;
  idOfMainLayout?: string;
  userProfileExists: boolean;
  mainLayoutExists: boolean;
  translationExists: boolean;
  dashboardViewExists: boolean;
  entityViewExists: boolean;
  pyloFlowsExists: boolean;
  templateResults: Record<string, TemplateCheckResult>;
  missingFieldsForDisplay: Array<{
    entityName: string;
    entityId: string;
    fields: Array<{
      name: string;
      data_type: string;
    }>;
    relations: Array<{
      field_name: string;
      type: "OneToOne" | "OneToMany" | "ManyToOne" | "ManyToMany";
      target_entity_id: string;
      target_field_name?: string;
    }>;
  }>;
}

const entityListQueryDocument = gql`
  query EntityList {
    entityList(pagination: { page: 1, per_page: 1000 }) {
      data {
        name
        id
        entity_fields {
          data {
            name
            id
          }
        }
        entity_relations(pagination: { page: 1, per_page: 1000 }) {
          data {
            id
            type
            field_name
            target_field_name
            entity {
              data {
                id
                name
              }
            }
            target_entity {
              data {
                id
                name
              }
            }
            created_at
            updated_at
          }
        }
      }
    }
  }
`;

export const checkIfAdminDashboardEntitiesExists = (
  entities: Array<ActualEntity> | undefined,
  bindings?: RoleBindings,
): AdminDashboardCheckResult | null => {
  if (!entities) {
    return null;
  }

  const templateResults: Record<string, TemplateCheckResult> = {};
  const missingFieldsForDisplay: AdminDashboardCheckResult["missingFieldsForDisplay"] = [];

  for (const feature of featureSpecs) {
    const plan = planFeature(feature, entities, bindings);
    const primarySpec = feature.entities.find((entity) => entity.role === feature.primaryRole);
    const primary = primarySpec ? resolveRole(primarySpec, entities, bindings) : undefined;

    for (const addition of plan.addFields) {
      missingFieldsForDisplay.push({
        entityName: addition.entityName,
        entityId: addition.entityId,
        fields: addition.fields,
        relations: [],
      });
    }
    for (const addition of plan.addRelations) {
      missingFieldsForDisplay.push({
        entityName: addition.entityName,
        entityId: addition.entityId,
        fields: [],
        relations: addition.relations,
      });
    }

    templateResults[feature.key] = {
      entityExists: !!primary,
      entityId: primary?.id,
      satisfied: isPlanEmpty(plan),
      missingFields: plan.addFields.flatMap((addition) => addition.fields),
      missingRelations: plan.addRelations.flatMap((addition) => addition.relations),
    };
  }

  // Legacy boolean checks for backward compatibility
  const mainLayoutExists = templateResults["MainLayout"]?.entityExists || false;
  const folderStructureExists = isTemplateActive(templateResults["MainLayout"]);
  const idOfMainLayout = templateResults["MainLayout"]?.entityId;
  const userProfileExists = templateResults["UserProfile"]?.entityExists || false;
  const translationExists = templateResults["Language"]?.entityExists || false;
  const dashboardViewExists = templateResults["DashboardView"]?.entityExists || false;
  const entityViewExists = templateResults["EntityView"]?.entityExists || false;
  const pyloFlowsExists = templateResults["FlowNodeMeta"]?.entityExists || false;

  return {
    folderStructureExists,
    idOfMainLayout,
    userProfileExists,
    mainLayoutExists,
    translationExists,
    dashboardViewExists,
    entityViewExists,
    pyloFlowsExists,
    templateResults,
    missingFieldsForDisplay,
  };
};

const fetchEntities = async (): Promise<Array<ActualEntity>> => {
  const client = createGraphQLClient({ endpointName: "entityListForTemplates" });
  const res = (await client.request(entityListQueryDocument, {})) as {
    entityList: { data: Array<ActualEntity> };
  };
  return res.entityList.data;
};

export const addMissingFields = async (
  missingFieldsData: AdminDashboardCheckResult["missingFieldsForDisplay"],
) => {
  try {
    for (const entityData of missingFieldsData) {
      await addFieldsToEntity(entityData.entityId, entityData.fields);
      await addRelationsToEntity(entityData.entityId, entityData.relations);
    }
    return true;
  } catch (error) {
    console.error("error adding missing fields", error);
    throw Error("error adding missing fields");
  }
};

/** Depth-first over `dependsOn`, so a host entity exists before anything registers on it. */
const withDependencies = (
  features: Array<FeatureSpec>,
  seen = new Set<string>(),
): Array<FeatureSpec> =>
  features.flatMap((feature) => {
    if (seen.has(feature.key)) return [];
    seen.add(feature.key);
    const dependencies = (feature.dependsOn ?? []).flatMap((key) => {
      const dependency = featureSpecs.find((candidate) => candidate.key === key);
      return dependency ? [dependency] : [];
    });
    return [...withDependencies(dependencies, seen), feature];
  });

export const applyTemplates = async ({ templateName }: { templateName?: string }) => {
  // A named call activates exactly that feature (optional ones included). The
  // no-arg "Activate all" only applies required features, never opt-in ones.
  const requested = templateName
    ? featureSpecs.filter((feature) => feature.key === templateName)
    : featureSpecs.filter((feature) => !feature.optional);

  if (templateName && requested.length === 0) {
    throw Error("template not found");
  }

  try {
    // Refetched per feature: each one changes the schema the next reads.
    for (const feature of withDependencies(requested)) {
      await applyFeature(feature, await fetchEntities());
    }
  } catch (error) {
    console.error("error applying templates", error);
    throw Error("error applying templates");
  }

  return true;
};
